import { Button, Switch, Table, InputRef, Space, Input, message } from "antd";
import { ColumnsType } from "antd/es/table";
import { AdvisorDataType } from "./types";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import type { FilterDropdownProps } from "antd/es/table/interface";
import type { ColumnType } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { apiCall } from "../../../utils/apiCall";

interface TabsProps {
  type: string;
  data: AdvisorDataType[];
  fetchData: any;
}

const TabContent = ({ type, data, fetchData }: TabsProps) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const navigate = useNavigate();

  const searchInput = useRef<InputRef>(null);
  const handleSearch = (
    selectedKeys: any,
    confirm: Function,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: Function) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex: string): ColumnType<any> => {
    const searchProps: ColumnType<any> = {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(
                e.target.value ? [e.target.value] : ([] as string[])
              )
            }
            onPressEnter={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                handleSearch(selectedKeys as string[], confirm, dataIndex)
              }
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            {/* <Button
              type="link"
              size="small"
              onClick={() => {
                confirm({ closeDropdown: false });
                setSearchText(selectedKeys[0] as string);
                setSearchedColumn(dataIndex);
              }}
            >
              Filter
            </Button> */}
            <Button type="link" size="small" onClick={close}>
              close
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase()),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    };

    if (dataIndex !== "companyNumber") {
      searchProps.render = (text) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ""}
          />
        ) : (
          text
        );
    }

    return searchProps;
  };

  const getBranchFilters = (data: AdvisorDataType[]) => {
    return Array.from(new Set(data.map((item) => item.branchCode))).map(
      (branchCode) => ({
        text: branchCode,
        value: branchCode,
      })
    );
  };

  const columns: ColumnsType<AdvisorDataType> = [
    {
      title: "#",
      render: (_, record) => record.rowNumber,
    },
    {
      title: "사번 ID",
      key: "companyNumber",
      dataIndex: "companyNumber",
      width: 100,
      render: (v) => (
        <Button
          type="link"
          onClick={() => {
            navigate(`/sales188/advisor-management/${v}`);
          }}
        >
          {v}
        </Button>
      ),
      ...getColumnSearchProps("companyNumber"),
    },
    {
      title: "이름",
      key: "name",
      dataIndex: "name",
      width: 80,
      ...getColumnSearchProps("name"),
    },
    {
      title: "지사",
      key: "branchCode",
      dataIndex: "branchCode",
      width: 180,
      filters: getBranchFilters(data),
      onFilter: (value, record) => record.branchCode === value,
      render: (_, record) => `${record.branchName}(${record.branchCode})`,
    },
    {
      title: "전화번호",
      key: "phoneNumber",
      dataIndex: "phoneNumber",
      width: 140,
      ...getColumnSearchProps("phoneNumber"),
    },
    {
      title: "K비용제외",
      key: "excludeKeepCost",
      dataIndex: "excludeKeepCost",
      render: (v) => (v ? "제외" : ""),
    },
    ...(type !== "resigned"
      ? [
          {
            title: "활동승인여부",
            key: "isApproved",
            dataIndex: "isApproved",
            render: (v: boolean, record: AdvisorDataType) => (
              <Switch
                checked={v}
                checkedChildren="승인됨"
                unCheckedChildren="취소됨"
                onChange={(e) => {
                  changeApproveStatus(e, record.companyNumber);
                }}
              />
            ),
          },
        ]
      : []),
  ];

  const header = {
    headers: {
      "x-api-key": process.env.REACT_APP_X_API_KEY,
    },
  };

  const changeApproveStatus = async (
    boolean: boolean,
    companyNumber: string
  ) => {
    const data = await apiCall({
      method: "PATCH",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN +
        `/advisors/admin/${companyNumber}/approval`,
      payload: {
        isApproved: boolean,
      },
    });
    if (data.status !== "SUCCESS") {
      message.error(renderAntDMessageConfig(data.response.data.status));
      return;
    } else {
      message.success(
        renderAntDMessageConfig(
          boolean
            ? "어드바이저의 활동이 승인되었습니다."
            : "승인이 취소되었습니다."
        )
      );
      fetchData();
    }
  };

  return (
    <div className="tab-container">
      <div className="flex items-center justify-between p-5">
        <p className="text-base">
          {type === "general"
            ? "사번대장"
            : type === "head"
            ? "본사"
            : "퇴사자"}
        </p>
        <div className="flex">
          <img
            className="icon cursor-pointer"
            alt="refresh-icon"
            src={require("../../../assets/icons/refresh.png")}
          />
          <img
            className="icon"
            alt="expand-icon"
            src={require("../../../assets/icons/expand.png")}
          />
        </div>
      </div>
      <div className="p-2">
        <Table
          columns={columns}
          dataSource={data.map((item, index) => ({
            ...item,
            rowNumber: index + 1,
          }))}
          pagination={{
            showTotal: (total) => `Total ${total} items`,
          }}
        />
      </div>
    </div>
  );
};

export default TabContent;
